export type HarRequestForClassification = {
  method?: string;
  headers?: Array<{ name: string; value?: string }>;
  postData?: { text?: string; mimeType?: string };
};

export type GenericXmlMimeHint = 'soap+xml' | 'xml' | 'text-xml';

export type GenericXmlClassification =
  | { isXml: true; mimeHint: GenericXmlMimeHint }
  | { isXml: false };

export type SpecializedXmlKind = 'SOAP' | 'XML_RPC' | 'XML';

export type SpecializedXmlClassification = {
  kind: SpecializedXmlKind;
  operationName: string;
  displayName: string;
} | null;

export type ClassifyAsSoapResult =
  | { kind: 'SOAP'; isSoap: true; operationName: string | null }
  | { kind: null; isSoap: false; operationName: null };

export type ClassifyAsXmlRpcResult =
  | { kind: 'XML_RPC'; isXmlRpc: true; operationName: string }
  | { kind: null; isXmlRpc: false; operationName: null };

export type ClassifyAsXmlResult =
  | { kind: 'XML'; isXml: true; operationName: string }
  | { kind: null; isXml: false; operationName: null };

export function classifyXmlEndpoint(
  request: HarRequestForClassification,
): GenericXmlClassification {
  if (toUpperMethod(request.method) !== 'POST') {
    return notXml();
  }

  const contentType = findHeaderValue(request.headers, 'content-type') ?? '';
  const mimeLower = contentType.toLowerCase();
  const postMimeLower = request.postData?.mimeType?.toLowerCase() ?? '';
  const postText = request.postData?.text?.trim() ?? '';

  if (
    mimeLower.includes('application/soap+xml') ||
    postMimeLower.includes('application/soap+xml')
  ) {
    return { isXml: true, mimeHint: 'soap+xml' };
  }

  if (
    mimeLower.includes('application/xml') ||
    postMimeLower.includes('application/xml')
  ) {
    return { isXml: true, mimeHint: 'xml' };
  }

  if (mimeLower.includes('text/xml') || postMimeLower.includes('text/xml')) {
    return { isXml: true, mimeHint: 'text-xml' };
  }

  if (postText.startsWith('<?xml') || /^<[A-Za-z_]/.test(postText)) {
    return { isXml: true, mimeHint: 'xml' };
  }

  return notXml();
}

export function classifySpecializedXmlEndpoint(
  request: HarRequestForClassification,
): SpecializedXmlClassification {
  const soap = classifyAsSoap(request);

  if (soap.isSoap) {
    const label = soap.operationName ?? 'unknown';

    return {
      kind: 'SOAP',
      operationName: `soap:${label}`,
      displayName: label,
    };
  }

  const xmlRpc = classifyAsXmlRpc(request);

  if (xmlRpc.isXmlRpc) {
    return {
      kind: 'XML_RPC',
      operationName: `xml-rpc:${xmlRpc.operationName}`,
      displayName: xmlRpc.operationName,
    };
  }

  const xml = classifyAsXml(request);

  if (xml.isXml) {
    return {
      kind: 'XML',
      operationName: `xml:${xml.operationName}`,
      displayName: stripXmlRootPrefix(xml.operationName),
    };
  }

  return null;
}

export function classifyAsSoap(
  request: HarRequestForClassification,
): ClassifyAsSoapResult {
  if (toUpperMethod(request.method) !== 'POST') {
    return notSoap();
  }

  const soapActionRaw = findHeaderValue(request.headers, 'soapaction');
  const contentType = findHeaderValue(request.headers, 'content-type') ?? '';
  const mimeLower = contentType.toLowerCase();
  const postText = request.postData?.text?.trim() ?? '';

  const hasSoapActionHeader = soapActionRaw !== undefined;
  const isSoapXmlMime = mimeLower.includes('application/soap+xml');
  const looksLikeEnvelope =
    postText.length > 0 && hasSoapEnvelopeRoot(postText);

  if (!hasSoapActionHeader && !isSoapXmlMime && !looksLikeEnvelope) {
    return notSoap();
  }

  let operationName: string | null = null;

  if (hasSoapActionHeader && soapActionRaw !== undefined) {
    operationName = normalizeSoapAction(soapActionRaw);
  }

  if (!operationName && isSoapXmlMime) {
    operationName = extractSoap12ActionFromContentType(contentType);
  }

  if (!operationName && postText) {
    operationName = extractOperationNameFromSoapEnvelopeBody(postText);
  }

  return { kind: 'SOAP', isSoap: true, operationName };
}

export function classifyAsXmlRpc(
  request: HarRequestForClassification,
): ClassifyAsXmlRpcResult {
  if (toUpperMethod(request.method) !== 'POST') {
    return notXmlRpc();
  }

  const postText = request.postData?.text?.trim() ?? '';

  if (!postText || !hasXmlRpcShape(request, postText)) {
    return notXmlRpc();
  }

  const operationName = extractXmlRpcMethodName(postText);

  if (!operationName) {
    return notXmlRpc();
  }

  return { kind: 'XML_RPC', isXmlRpc: true, operationName };
}

export function classifyAsXml(
  request: HarRequestForClassification,
): ClassifyAsXmlResult {
  const emptyResult: ClassifyAsXmlResult = {
    kind: null,
    isXml: false,
    operationName: null,
  };

  if (toUpperMethod(request.method) !== 'POST') {
    return emptyResult;
  }

  const postText = request.postData?.text?.trim() ?? '';

  if (!postText || !looksLikeXmlPayload(request, postText)) {
    return emptyResult;
  }

  const operationName = extractXmlOperationName(postText);

  if (!operationName) {
    return emptyResult;
  }

  return { kind: 'XML', isXml: true, operationName };
}

export function toEndpointOperationFields(
  classification: SpecializedXmlClassification,
): {
  soapOperationName: string | null;
  xmlOperationName: string | null;
  xmlOperationDisplayName: string | null;
} {
  if (!classification) {
    return {
      soapOperationName: null,
      xmlOperationName: null,
      xmlOperationDisplayName: null,
    };
  }

  if (classification.kind === 'SOAP') {
    return {
      soapOperationName: classification.displayName,
      xmlOperationName: null,
      xmlOperationDisplayName: null,
    };
  }

  return {
    soapOperationName: null,
    xmlOperationName: classification.operationName,
    xmlOperationDisplayName: classification.displayName,
  };
}

export function deriveXmlOperationLabel(
  operationName: string | null | undefined,
): string | null {
  if (!operationName) {
    return null;
  }

  if (operationName.startsWith('xml-rpc:')) {
    return operationName.slice('xml-rpc:'.length);
  }

  if (operationName.startsWith('xml:')) {
    return stripXmlRootPrefix(operationName.slice('xml:'.length));
  }

  if (operationName.startsWith('soap:')) {
    return operationName.slice('soap:'.length);
  }

  return operationName;
}

export function formatEndpointOperationHashSuffix(args: {
  graphqlOperationName?: string | null;
  soapOperationName?: string | null;
  xmlOperationName?: string | null;
  mcpOperationName?: string | null;
  grpcServiceName?: string | null;
  grpcMethodName?: string | null;
}): string {
  const {
    graphqlOperationName,
    soapOperationName,
    xmlOperationName,
    mcpOperationName,
    grpcServiceName,
    grpcMethodName,
  } = args;
  const grpcRpcName = [grpcServiceName?.trim(), grpcMethodName?.trim()]
    .filter(Boolean)
    .join('/');

  return `${graphqlOperationName ? ':' + graphqlOperationName : ''}${
    soapOperationName ? ':soap:' + soapOperationName : ''
  }${xmlOperationName ? ':xml:' + xmlOperationName : ''}${
    mcpOperationName ? ':mcp:' + mcpOperationName : ''
  }${grpcRpcName ? ':grpc:' + grpcRpcName : ''}`;
}

function stripXmlRootPrefix(operationName: string): string {
  const idx = operationName.indexOf(':');

  return idx === -1 ? operationName : operationName.slice(idx + 1);
}

function notXml(): GenericXmlClassification {
  return { isXml: false };
}

function notSoap(): ClassifyAsSoapResult {
  return { kind: null, isSoap: false, operationName: null };
}

function notXmlRpc(): ClassifyAsXmlRpcResult {
  return { kind: null, isXmlRpc: false, operationName: null };
}

function toUpperMethod(method: string | undefined): string | undefined {
  return method?.toUpperCase();
}

function findHeaderValue(
  headers: HarRequestForClassification['headers'],
  nameLower: string,
): string | undefined {
  const target = nameLower.toLowerCase();
  const found = headers?.find((header) => header.name.toLowerCase() === target);

  return found?.value;
}

function hasSoapEnvelopeRoot(text: string): boolean {
  if (!text.startsWith('<')) {
    return false;
  }

  return /<(?:[\w.:-]+:)?Envelope[\s>/]/.test(text);
}

function normalizeSoapAction(raw: string): string | null {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return null;
  }

  const unquoted = trimmed.replace(/^["']|["']$/g, '');

  if (!unquoted || unquoted === '""') {
    return null;
  }

  if (unquoted.includes('#')) {
    const parts = unquoted.split('#');

    return parts[parts.length - 1] || null;
  }

  if (unquoted.includes('/')) {
    const segments = unquoted.split('/').filter(Boolean);

    return segments[segments.length - 1] || null;
  }

  return unquoted;
}

function extractSoap12ActionFromContentType(
  contentType: string,
): string | null {
  const actionMatch = contentType.match(/action\s*=\s*["']([^"']+)["']/i);

  if (!actionMatch?.[1]) {
    return null;
  }

  return normalizeSoapAction(actionMatch[1]);
}

function extractOperationNameFromSoapEnvelopeBody(text: string): string | null {
  const bodyMatch = text.match(
    /<(?:[\w.:-]+:)?Body[^>]*>([\s\S]*?)<\/(?:[\w.:-]+:)?Body>/i,
  );

  if (!bodyMatch?.[1]) {
    return null;
  }

  const inner = bodyMatch[1].trim();
  const firstTag = inner.match(/<([\w.:-]+)(?:\s|\/|>)/);

  if (!firstTag?.[1]) {
    return null;
  }

  return stripXmlNamePrefix(firstTag[1]);
}

function hasXmlRpcShape(
  request: HarRequestForClassification,
  postText: string,
): boolean {
  const contentType = findHeaderValue(request.headers, 'content-type') ?? '';
  const mimeLower = contentType.toLowerCase();
  const mimeType = request.postData?.mimeType?.toLowerCase() ?? '';

  if (mimeLower.includes('application/soap+xml')) {
    return false;
  }

  const xmlContentType =
    mimeLower.includes('application/xml') ||
    mimeLower.includes('text/xml') ||
    mimeType.includes('application/xml') ||
    mimeType.includes('text/xml') ||
    mimeLower.includes('application/xml-rpc') ||
    mimeLower.includes('text/xml-rpc');

  if (!xmlContentType && !postText.startsWith('<')) {
    return false;
  }

  return /<methodCall[\s>]/i.test(postText);
}

function extractXmlRpcMethodName(postText: string): string | null {
  const methodNameMatch = postText.match(
    /<methodName[^>]*>\s*([^<\s][^<]*?)\s*<\/methodName\s*>/i,
  );
  const raw = methodNameMatch?.[1]?.trim();

  return raw && raw.length > 0 ? raw : null;
}

function looksLikeXmlPayload(
  request: HarRequestForClassification,
  postText: string,
): boolean {
  if (!postText.startsWith('<')) {
    return false;
  }

  const contentType = findHeaderValue(request.headers, 'content-type') ?? '';
  const mimeLower = contentType.toLowerCase();
  const mimeType = request.postData?.mimeType?.toLowerCase() ?? '';

  if (mimeLower.includes('application/soap+xml')) {
    return false;
  }

  return (
    mimeLower.includes('application/xml') ||
    mimeLower.includes('text/xml') ||
    mimeType.includes('application/xml') ||
    mimeType.includes('text/xml')
  );
}

function extractXmlOperationName(postText: string): string | null {
  const withoutDeclaration = postText.replace(/^\s*<\?xml[^?]*\?>\s*/i, '');
  const rootMatch = withoutDeclaration.match(/^<([^\s/>]+)([^>]*?)\/?>/);

  if (!rootMatch?.[1]) {
    return null;
  }

  const rootLocalName = stripXmlNamePrefix(rootMatch[1]);
  const attributes = rootMatch[2] ?? '';
  const typeMatch = attributes.match(/\btype\s*=\s*(['"])([^'"]+)\1/i);

  if (!typeMatch?.[2]) {
    return null;
  }

  return `${rootLocalName}:type=${typeMatch[2]}`;
}

function stripXmlNamePrefix(name: string): string {
  return name.includes(':') ? (name.split(':').pop() ?? name) : name;
}
